import { FC } from 'react'
import styled from 'styled-components'
import useTranslation from 'next-translate/useTranslation'
import { Flex } from '@/components/atoms/Grid'

import { IconButtonCapitalized } from '@/components/molecules/iconButtons'
import { useRouter } from 'next/router'
import { Routes } from '@/core/routes'
import { GoogleIconDefault } from '@/components/atoms/icons/GoogleIconDefault'

import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google'
import { notifyError } from '@/core/toast'

import { useAuth } from '@/providers/authProvider'

type GoogleResponse = {
  access_token?: string
  token_type?: string
  expires_in?: number
  scope?: string
  authuser?: string
  prompt?: string
}

const GoogleLoginButton: FC = () => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const isLoginPage = router.pathname === Routes.login

  const { loginByGoogle } = useAuth()

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: (responseGoogle: GoogleResponse | null) => {
      if (responseGoogle?.access_token) {
        loginByGoogle(responseGoogle?.access_token)
      }
    },
    onError: (error) => {
      const errorMessage = t('Login.loginFailed')
      notifyError(errorMessage)
      localStorage.set('Error_google_login', JSON.stringify(error))
    }
  })

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      width={{ mobile: '100%', tablet: '100%', desktop: 500 }}
    >
      <GoogleButton
        onClick={handleGoogleLogin}
        IconElement={<GoogleIconDefault />}
        title={
          isLoginPage ? t('LoginPage.Gmail.title') : t('Register.Gmail.title')
        }
        isSocialButton
      />
    </Flex>
  )
}

// The provider loads the Google Sign-In script, only pages with these buttons need it
const LoginSocialButtons: FC = () => (
  <GoogleOAuthProvider clientId={process.env.GOOGLE_CLIENT_ID}>
    <GoogleLoginButton />
  </GoogleOAuthProvider>
)

export default LoginSocialButtons

const GoogleButton = styled(IconButtonCapitalized)`
  align-items: center;
  height: 54px;
  margin-top: 16px;
  width: 100%;
  border: 1px solid ${(props) => props.theme.colors.googleButtonBorder};
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.onBackground};
  &:hover {
    background-color: ${(props) => props.theme.colors.listItemSeparator};
  }
`
